import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Skeleton,
  Stack
} from '@mui/material';
import adCenterApi from '../adCenterApi';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../CoreDataService';

export default function ViewAudience() {
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Move fetchAudiences outside useEffect so it can be reused
  const fetchAudiences = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adCenterApi.post('/getAudienceByBroker', {
        adminUser: localStorage.getItem('uuid'),
        brokerId: localStorage.getItem('brokerId'),
      });
      if (response.data && response.data.statusCode === 0) {
        setAudiences(response.data.audienceDetails || []);
      } else {
        setError(response.data.message || 'Failed to fetch audiences');
      }
    } catch (err) {
      setError('Error fetching audiences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudiences();
  }, []);

  const handleDelete = async (row) => {
    if (!window.confirm('Are you sure you want to delete this audience?')) return;
    try {
      setLoading(true);
      const payload = {
        adminUser: localStorage.getItem('uuid'),
        audienceId: row.audienceId,
        brokerId: localStorage.getItem('brokerId'),
        gender: row.gender,
        minAge: row.minAge,
        maxAge: row.maxAge,
        location: row.location,
        role: row.role,
        audienceType: row.audienceType,
        deviceType: row.deviceType,
        action: 'DELETE',
      };
      const response = await adCenterApi.post('/modifyAudience', payload);
      if (response.data && response.data.statusCode === 0) {
        showToast('success', 'Audience deleted successfully!');
        // Refresh list
        fetchAudiences();
      } else {
        showToast('error', response.data.message || 'Failed to delete audience');
      }
    } catch (err) {
      showToast('error', 'Error deleting audience');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mb: 6 }}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 1 }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold" color="primary.main">
              Audiences
            </Typography>
            <Typography variant="body1" color="text.secondary" mt={0.5} sx={{ fontStyle: 'italic' }}>
              View and manage all your audiences in one place.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Card elevation={6} sx={{ borderRadius: 3, boxShadow: 6 }}>
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              {loading ? (
                <Stack spacing={2}>
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
                  ))}
                </Stack>
              ) : error ? (
                <Typography color="error">{error}</Typography>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#222b36' }}>
                        <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Audience Name</TableCell>

                        <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Gender</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Age</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Audience Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Operating System</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {audiences.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">No audiences found.</TableCell>
                        </TableRow>
                      ) : (
                        audiences.map((row, idx) => (
                          <TableRow key={row.audienceId || idx} hover sx={{ transition: 'background 0.2s', '&:hover': { backgroundColor: 'grey.100' } }}>
                            <TableCell sx={{ fontWeight: 500 }}>{row.audienceName || '-'}</TableCell>

                            <TableCell sx={{ fontWeight: 500 }}>{row.gender || '-'}</TableCell>
                            <TableCell>{row.minAge && row.maxAge ? `${row.minAge} - ${row.maxAge}` : '-'}</TableCell>
                            <TableCell>{row.location || '-'}</TableCell>
                            <TableCell>{row.role || '-'}</TableCell>
                            <TableCell>{row.audienceType || '-'}</TableCell>
                            <TableCell>{row.deviceType || '-'}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                                <Button variant="outlined" color="primary" size="small" sx={{ fontWeight: 'bold', borderRadius: 2 }}
                                  onClick={() => navigate('/edit-audience', { state: { audience: row } })}
                                >Edit</Button>
                                <Button variant="outlined" color="error" size="small" sx={{ fontWeight: 'bold', borderRadius: 2 }}
                                  onClick={() => handleDelete(row)}
                                >Delete</Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Container>
  );
}
